'use client';

import { useEffect } from 'react';
import { useAuthStore } from '@/shared/stores/auth-store';
import { clearSentryUser, identifySentryUser } from '@/shared/lib/sentry-user';

/**
 * Safety-net component that keeps Sentry user context in sync with the auth store.
 *
 * Primary identification happens in auth-store actions (login, register, etc.)
 * for immediate context. This component is a redundant safety net that catches
 * edge cases like new auth flows that forget to call identifySentryUser().
 *
 * Race condition safety: The effect gates on `isInitialized`, which is only set
 * to `true` after `fetchUser()` completes. This means the effect never fires
 * with stale/optimistic data from localStorage rehydration.
 */
export function SentryUserIdentifier() {
  const user = useAuthStore((s) => s.user);
  const isSubscribed = useAuthStore((s) => s.isSubscribed);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  useEffect(() => {
    if (!isInitialized) return;

    if (isAuthenticated && user) {
      identifySentryUser(user.id, isSubscribed);
    } else {
      clearSentryUser();
    }
  }, [isInitialized, isAuthenticated, user, isSubscribed]);

  return null;
}
